import { ChangeDetectorRef, Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { User } from 'src/app/core/models/user.model';
import { StorageService } from 'src/app/core/services/storage.service';
import { UserService } from 'src/app/core/services/user.service';
import { EditProfileSuccessChangeComponent } from '../../dialog/edit-profile-success-change/edit-profile-success-change.component';
import { ErrorModalComponent } from 'src/app/shared/components/simple/error-modal/error-modal.component';

import { TranslateService } from '@ngx-translate/core';
import { NgxMatIntlTelInputComponent } from 'ngx-mat-intl-tel-input';
import { Subject, takeUntil, tap } from 'rxjs';
import { Pii } from 'src/app/core/models/pii.model';
import { PiiService } from 'src/app/core/services/pii.service';
import { KycService } from 'src/app/core/services/kyc.service';
import { StructuredAddress } from 'src/app/core/services/address.service';
import { ProfileAddressValidationService } from 'src/app/core/services/profile-address-validation.service';
import { ProfileAddressState } from 'src/app/shared/components/smart/profile-address-input/profile-address-input.component';
import { v4 as uuid } from 'uuid';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import {
  MAT_MOMENT_DATE_ADAPTER_OPTIONS,
  MomentDateAdapter,
} from '@angular/material-moment-adapter';
import * as moment from 'moment';

const DATE_PICKER_FORMATS = {
  parse: {
    dateInput: 'DD/MM/YYYY',
  },
  display: {
    dateInput: 'DD/MM/YYYY',
    monthYearLabel: 'MMMM YYYY',
    dateA11yLabel: 'LL',
    monthYearA11yLabel: 'MMMM YYYY',
  },
};

@Component({
  selector: 'app-edit-profile',
  templateUrl: './edit-profile.component.html',
  styleUrls: ['./edit-profile.component.scss'],
  providers: [
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_FORMATS },
  ],
})
export class EditProfileComponent implements OnInit, OnDestroy {
  user: User | null = null;
  phoneNumberCountryPrefix = '+55';

  // Form validation properties
  isFormValid = false;
  formErrors: { [key: string]: boolean } = {};

  languageOptions = [
    { value: 'PT', text: 'Português (BR)' },
    { value: 'EN', text: 'English' },
  ];

  @ViewChild('phoneinput') phoneInput!: NgxMatIntlTelInputComponent;

  pii!: Pii;

  profileImg: string | null = null;
  profileName = '-';
  profileLoading = false;
  isUploading = false;
  // TODO: Implement the user data

  name: string = '';

  language = 'EN';

  preferredCountries = ['gb', 'us'];

  form: FormGroup;

  // Getters para facilitar acesso aos controles
  get formControls() {
    return {
      first_name: this.form.get('first_name') as FormControl,
      surname: this.form.get('surname') as FormControl,
      date_of_birth: this.form.get('date_of_birth') as FormControl,
      phone: this.form.get('phone') as FormControl,
      email: this.form.get('email') as FormControl,
      language: this.form.get('language') as FormControl,
    };
  }

  isSaving = false;
  isKycValidated = false;

  // CPK-4914: the address comes from app-profile-address-input (WORLD_ADDRESS), not form controls.
  addressState: ProfileAddressState = { status: 'unchanged' };

  private destroySubject = new Subject<void>();
  private initialPrimaryAddressSnapshot = '';

  constructor(
    public dialog: MatDialog,
    private us: UserService,
    private ss: StorageService,
    private piis: PiiService,
    private snackbar: MatSnackBar,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private kycService: KycService,
    private profileAddressValidation: ProfileAddressValidationService
  ) {
    console.log('Normal edit profile');

    // Inicializar PII com estrutura básica para evitar campos ocultos
    this.pii = {
      id: '',
      date_of_birth: '',
      legal_name: '',
      id_document_type: '',
      id_document_value: '',
      attributes: {
        first_name: '',
        surname: ''
      },
      primary_address: {
        county: '',
        postcode: '',
        address_line_1: '',
        state: '',
        postalCode: '',
        streetName: '',
        streetNumber: '',
        country: '',
        buildingName: '',
        unitNumber: '',
        city: '',
        neighborhood: ''
      },
      contacts: {
        phone: '',
        email: ''
      },
      documents: '',
      user_id: '',
      created_at: new Date()
    };

    // Inicializar FormGroup
    this.form = new FormGroup({
      first_name: new FormControl('', [Validators.required]),
      surname: new FormControl('', [Validators.required]),
      date_of_birth: new FormControl(''),
      phone: new FormControl(''),
      email: new FormControl(''),
      language: new FormControl('EN'),
    });

    this._initValidation();
  }

  private _initValidation() {
    this.createErrorSubscription('first_name');
    this.createErrorSubscription('surname');
  }

  private createErrorSubscription(
    formControlName: 'first_name' | 'surname'
  ) {
    this.formControls[formControlName]?.valueChanges
      .pipe(
        tap(() => {
          this.formErrors[formControlName] =
            !!this.formControls[formControlName].errors?.['required'];
          // Recompute overall validity when any control changes
          this.validateFields();
        }),
        takeUntil(this.destroySubject)
      )
      .subscribe();
  }

  private validateFields(): void {
    this.isFormValid =
      !this.formErrors['first_name'] &&
      !this.formErrors['surname'] &&
      this.addressState.status !== 'invalid';
  }

  onAddressStateChange(state: ProfileAddressState): void {
    this.addressState = state;
    this.validateFields();
  }

    /** Returns array of human friendly missing required fields */
  getMissingRequiredFields(): string[] {
    const missing: string[] = [];
    if (this.formControls['first_name'].invalid) missing.push(this.translateService.instant('EditProfileFirstName'));
    if (this.formControls['surname'].invalid) missing.push(this.translateService.instant('EditProfileSurname'));
    if (this.addressState.status === 'invalid') missing.push(this.translateService.instant('EditProfileAddress'));
    return missing;
  }

  /** Returns a tooltip string listing missing fields (or empty) */
  getMissingFieldsTooltip(): string {
    const missing = this.getMissingRequiredFields();
    if (missing.length === 0) return '';
    return this.translateService.instant('MissingFields') + ': ' + missing.join(', ');
  }

  async ngOnInit(): Promise<void> {
    const user = await this.us.getCurrentUser();
    this.user = user;
    this.buildProfile();
    console.log('user', user);

    if (!user) {
      console.error('No user loaded', user);
      return;
    }

    this.language = this.normalizeLanguage(user.language);

    const pii = await this.piis.getFromUserId(user.id!);
    this.pii = pii;
    if (!this.pii.attributes) {
      // Se attributes não existe, extrair de display_name
      const displayName = user.display_name || '';
      const nameParts = displayName.split(' ').filter(Boolean);
      const fname = nameParts[0] || '';
      const surname = nameParts.length > 1 ? nameParts.slice(1).join(' ') : '';

      this.pii.attributes = {
        first_name: fname,
        surname: surname,
      };
    } else if (this.pii.attributes.middle_name || this.pii.attributes.last_name) {
      // Migração: se tem middle_name/last_name, converter para surname
      const fname = this.pii.attributes.first_name || '';
      const mname = this.pii.attributes.middle_name || '';
      const lname = this.pii.attributes.last_name || '';

      this.pii.attributes.first_name = fname;
      this.pii.attributes.surname = `${mname} ${lname}`.trim();
    }

    if (!this.pii.contacts) {
      console.log('[PROFILE] Did not have contacts', this.pii);
      this.pii.contacts = {
        phone: '',
        email: user.primary_email || '',
      };
    } else if (Array.isArray(this.pii.contacts)) {
      // CORREÇÃO: contacts veio como array do banco - converter para objeto
      console.log('[PROFILE] contacts is array, converting to object', this.pii.contacts);
      const firstContact = this.pii.contacts[0] || {};
      this.pii.contacts = {
        phone: firstContact.phone || '',
        email: firstContact.email || user.primary_email,
      };
    } else if (typeof this.pii.contacts === 'object') {
      // Garantir que tem phone e email
      this.pii.contacts = {
        phone: (this.pii.contacts as any).phone || '',
        email: (this.pii.contacts as any).email || user.primary_email,
      };
    }

    this.initialPrimaryAddressSnapshot = this.profileAddressValidation.normalizedAddressSnapshot(
      this.pii.primary_address as StructuredAddress
    );

    console.log('this.user', this.user);
    console.log('this.pii', this.pii);

    // Update form with user data
    this.updateFormWithUserData();

    // Check KYC status
    this.checkKycStatus(user.id!);

    // Forçar detecção de mudanças para garantir que os campos apareçam
    this.cdr.detectChanges();
  }

  async checkKycStatus(userId: string) {
    try {
      const checks = await this.kycService.getAllForUser(userId);
      const hasPassedKyc = checks.some((check) => check.stage === 'DONE' && check.passed);

      if (hasPassedKyc) {
        this.isKycValidated = true;
        this.formControls.first_name.disable();
        this.formControls.surname.disable();
        this.formControls.date_of_birth.disable();
      }
    } catch (error) {
      console.error('Error checking KYC status', error);
    }
  }

  ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }

  private updateFormWithUserData(): void {
    console.log('[PROFILE] 🔵 updateFormWithUserData called');

    // Usar setTimeout para garantir que o Angular processe o binding
    setTimeout(() => {
      // Update all fields at once usando patchValue no FormGroup
      const formData: any = {};

      // Name fields
      if (this.pii && this.pii.attributes) {
        formData.first_name = this.pii.attributes.first_name || '';
        formData.surname = this.pii.attributes.surname || '';
      }

      // Date of birth
      if (this.pii && this.pii.date_of_birth) {
        formData.date_of_birth = this.normalizeDateForPicker(
          this.pii.date_of_birth
        );
      }

      // Contact fields
      if (this.pii && this.pii.contacts) {
        formData.phone = (this.pii.contacts as any).phone || '';
        formData.email = (this.pii.contacts as any).email || this.user?.primary_email || '';
      }

      // Language
      if (this.user) {
        formData.language = this.normalizeLanguage(this.user.language);
        this.language = formData.language;
      }

      console.log('[PROFILE] 🟢 Patching form with data:', formData);
      this.form.patchValue(formData);

      console.log('[PROFILE] 🔵 Form values after patch:', this.form.getRawValue());

      // Forçar detecção de mudanças após atualizar o formulário
      this.cdr.detectChanges();
    }, 0);
  }

  changePhone(ev: any) {
    console.log('[PROFILE] changePhone', {
      ev,
      formValue: this.formControls.phone.value
    });
    // Atualizar PII se existir
    if (this.pii && this.pii.contacts) {
      (this.pii.contacts as any).phone = ev;
    }
  }

  buildProfile() {
    this.us.getCurrentUser().then((u) => {
      // Ckech user is logged

      console.log('User', u);

      if (u && u.avatar_url) {
        const imgUrl = `profile-pictures/${u.id}/${u.avatar_url}`;
        const bucket = 'profile-pictures';
        const path = `${u.id}/${u.avatar_url}`;

        console.log('Image URL', imgUrl);

        console.log('Bucket', bucket);
        console.log('Path', path);
        this.ss
          .signedUrl(bucket, path)
          .then((url) => {
            console.log('URL', url);
            if (url.error) {
              console.error('Error getting URL', url.error);
              return;
            }

            if (!url.data) {
              console.error('Error getting URL', url);
              return;
            }

            if (!url.data.signedUrl) {
              console.error('Error getting URL', url);
              return;
            }

            console.log('Signed URL', url.data.signedUrl);
            this.profileImg = url.data.signedUrl;

          })
          .catch((err) => {
            console.error('Error getting URL', err);
          });

        return;
      }

      this.profileImg = null;
      if (!u || !u.display_name) {
        this.profileName = '-';
      } else {
        // Get the First Letter by each word splitted by space and limit to 2.
        // Always get the the first and the last letter
        // Example: John Doe Mark => JM

        if (!u) {
          this.profileName = '-';
          return;
        }

        if (!u.display_name) {
          this.profileName = '-';
          return;
        }

        if (!u.display_name.trim()) {
          this.profileName = '-';
          return;
        }
        if (u.display_name === 'undefined') {
          this.profileName = '-';
          return;
        }

        const nameSplits = u.display_name.split(' ');
        const firstName = nameSplits[0];
        const lastName =
          nameSplits.length > 1 ? nameSplits[nameSplits.length - 1] : null;

        const firstLetter = firstName[0];
        const lastLetter = lastName ? lastName[0] : '';

        this.profileName = `${firstLetter}${lastLetter}`;
      }
      console.log('Profile Text', this.profileName);
    });
  }

  async changeProfileImg() {
    try {
      if (this.profileImg) {
        // TODO: Remove Image form bucket
        const bucket = 'profile-pictures';
        const path = `${this.user!.id}/${this.user!.avatar_url}`;

        this.isUploading = true;
        try {
          await this.ss.DeleteFilesInBucket(bucket, [path]);
          await this.us.updateUser({ ...this.user, avatar_url: null, pii: undefined } as any);
          this.buildProfile();
          this.snackbar.open(
            this.translateService.instant('ProfileImageDeleteSuccess'),
            this.translateService.instant('Close'),
            { duration: 3000 }
          );
        } catch (error) {
          console.error('Error deleting profile image', error);
          this.snackbar.open(
            this.translateService.instant('ProfileImageDeleteFailed'),
            this.translateService.instant('Close'),
            { duration: 5000 }
          );
        } finally {
          this.isUploading = false;
        }
      } else {
        // Pick new image
        const pickFile = document.createElement('input');
        pickFile.type = 'file';
        pickFile.accept = 'image/png,image/jpeg,application/pdf';
        pickFile.click();

        pickFile.onchange = async () => {
          const file = pickFile.files?.[0];
          if (!file) return;

          console.log('File', file);

          // Validate file type
          const allowedTypes = [
            'image/jpeg',
            'image/jpg',
            'image/png',
            'application/pdf',
          ];
          if (!allowedTypes.includes(file.type)) {
            this.snackbar.open(
              this.translateService.instant('FileTypeNotAllowed'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
            return;
          }

          // Validate file size (2MB = 2 * 1024 * 1024 bytes)
          const maxSize = 2 * 1024 * 1024; // 2MB
          if (file.size > maxSize) {
            this.snackbar.open(
              this.translateService.instant('FileTooBig'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
            return;
          }

          this.isUploading = true;
          this.snackbar.open(
            this.translateService.instant('ProfileImageUploading'),
            this.translateService.instant('Close'),
            { duration: 3000 }
          );

          try {
            const ext = file.name.split('.').pop();
            // Save on storage
            const bucket = 'profile-pictures';
            const imageID = `${uuid()}.${ext}`;

            console.log('Bucket', bucket);
            console.log('Image ID', imageID);
            const path = `${this.user!.id}/${imageID}`;

            const { data: img, error } = await this.ss.upload(
              bucket,
              path,
              file
            );

            if (error) {
              console.error('Error saving image', error);
              this.snackbar.open(
                this.translateService.instant('UploadFailed'),
                this.translateService.instant('Close'),
                { duration: 5000 }
              );
              return;
            }

            // Save Path on user
            await this.us.updateUser({ ...this.user, pii: undefined, avatar_url: imageID });
            this.buildProfile();
            this.snackbar.open(
              this.translateService.instant('ProfileImageUploadSuccess'),
              this.translateService.instant('Close'),
              { duration: 3000 }
            );
          } catch (error) {
            console.error('Error uploading profile image', error);
            this.snackbar.open(
              this.translateService.instant('UploadFailed'),
              this.translateService.instant('Close'),
              { duration: 5000 }
            );
          } finally {
            this.isUploading = false;
          }
        };
      }
    } catch (error) {
      console.error('Error changing profile image', error);
      this.snackbar.open(
        this.translateService.instant('UploadFailed'),
        this.translateService.instant('Close'),
        { duration: 5000 }
      );
      this.isUploading = false;
    }
  }

  async handleSubmit() {
    // Marcar todos os campos como touched para mostrar erros
    Object.values(this.formControls).forEach(control => {
      control.markAsTouched();
      control.updateValueAndValidity();
    });

    // Validate form before submission
    this.validateFields();

    if (!this.isFormValid) {
      const missing = this.getMissingRequiredFields();
      const message = missing.length
        ? `${this.translateService.instant('PleaseCompleteForm')}: ${missing.join(', ')}`
        : this.translateService.instant('PleaseCompleteForm');
      this.snackbar.open(message, this.translateService.instant('Close'), { duration: 7000 });
      return;
    }

    try {
      this.isSaving = true;
      // Optionally show a snackbar indicating saving started
      this.snackbar.open(this.translateService.instant('Saving'), undefined, { duration: 1500 });

      // Update PII with form values
      const dateOfBirthValue = this.serializeDateForApi(
        this.formControls.date_of_birth.getRawValue()
      );

      const formValues = {
        first_name: this.formControls.first_name.getRawValue(),
        surname: this.formControls.surname.getRawValue(),
        date_of_birth: dateOfBirthValue,
        phone: this.formControls.phone.getRawValue(),
        email: this.formControls.email.getRawValue(),
        language: this.formControls.language.getRawValue(),
      };

      // CPK-4914: an untouched address input keeps the stored address as is (no forced re-entry).
      const primaryAddress: StructuredAddress =
        this.addressState.status === 'changed'
          ? { ...this.addressState.address }
          : { ...((this.pii?.primary_address ?? {}) as StructuredAddress) };

      // Construir payload para enviar ao backend no formato NOVO
      const piiPayload: any = {
        first_name: formValues.first_name,
        surname: formValues.surname,
        date_of_birth: formValues.date_of_birth,
        contacts: {
          phone: formValues.phone,
          email: formValues.email,
        },
        primary_address: primaryAddress
      };

      const nuser: Partial<User> = {
        ...this.user,
        language: formValues.language,
      };

      delete this.pii.search_vector;
      delete this.pii.search_vector2;
      console.log('[PROFILE] Pre save', { nuser, piiPayload });

      const addressValidation = await this.profileAddressValidation.resolveBeforeProfileSave({
        userId: this.user!.id!,
        primaryAddress,
        initialPrimaryAddressSnapshot: this.initialPrimaryAddressSnapshot,
        existingAddressValidation:
          (this.pii?.primary_address as StructuredAddress | undefined)?.address_validation ?? null,
      });

      if (addressValidation.cancelled) {
        return;
      }

      piiPayload.primary_address = addressValidation.primaryAddress;

      // CPK-4239 fix: resolveBeforeProfileSave skips running a new check when
      // validation is disabled/unchanged, returning no `addressValidation` — falling
      // through here previously dropped the existing address_validation (raw_response,
      // llm_suspicion, retry_count) instead of leaving it untouched.
      const existingAddressValidation = (this.pii?.primary_address as StructuredAddress | undefined)
        ?.address_validation;
      if (existingAddressValidation) {
        piiPayload.primary_address.address_validation = existingAddressValidation;
      }
      if (addressValidation.addressValidation) {
        piiPayload.primary_address.address_validation = addressValidation.addressValidation;
      }

      console.log('saving nuser', nuser);
      const res = await this.us.updateUser(nuser, piiPayload);
      if (res) {
        this.initialPrimaryAddressSnapshot = this.profileAddressValidation.normalizedAddressSnapshot(
          piiPayload.primary_address
        );
        // Re-seeds the address input with what was saved (incl. a correction made in the prompt).
        this.pii.primary_address = piiPayload.primary_address;
      }
      this.openDialog();
    } catch (error) {
      console.error(error);
      this.openErrorDialog();
    } finally {
      // Ensure spinner is cleared and button re-enabled
      this.isSaving = false;
    }
  }

  private normalizeDateForPicker(
    value: string | Date | moment.Moment | null | undefined
  ): Date | null {
    const parsedDate = this.parseDateValue(value);
    return parsedDate ? parsedDate.toDate() : null;
  }

  private serializeDateForApi(
    value: string | Date | moment.Moment | null | undefined
  ): string | null {
    const parsedDate = this.parseDateValue(value);
    return parsedDate ? parsedDate.format('YYYY-MM-DD') : null;
  }

  /**
   * Parses user input accepting both ISO (yyyy-MM-dd) and locale (dd/MM/yyyy) formats
   * so the date picker never swaps day and month when users type a date manually.
   */
  private parseDateValue(
    value: string | Date | moment.Moment | null | undefined
  ): moment.Moment | null {
    if (!value) {
      return null;
    }

    if (moment.isMoment(value)) {
      return value.isValid() ? value.clone().startOf('day') : null;
    }

    if (value instanceof Date) {
      return moment(value).isValid() ? moment(value).startOf('day') : null;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (!trimmed) return null;

      const parsed = moment(trimmed, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
      return parsed.isValid() ? parsed.startOf('day') : null;
    }

    return null;
  }

  // cp_user.language comes in several spellings ('pt', 'PT', 'en', 'EN', 'English'); the
  // select only has 'PT' / 'EN', so anything else left the Language field blank.
  private normalizeLanguage(language: string | null | undefined): string {
    return (language || '').trim().toUpperCase().startsWith('PT') ? 'PT' : 'EN';
  }

  changeLang() {
    const lang = this.formControls.language.value || 'EN';
    console.log('[Edit profile] Change lang', lang);
    this.translateService.use(lang.toLowerCase());
    // Manter variável language sincronizada
    this.language = lang;
  }

  private openDialog() {
    const ref = this.dialog.open(EditProfileSuccessChangeComponent);
    return ref;
  }

  openErrorDialog() {
    const ref = this.dialog.open(ErrorModalComponent, {
      data: {
        title: 'ErrorWhenSaving',
        message: 'ErrorSavingChangesMessage',
      },
    });
    ref.componentInstance.title = 'ErrorWhenSaving';
    ref.componentInstance.message = 'ErrorSavingChangesMessage';
    return ref;
  }
}
